import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ADJUSTMENT_REQUEST_ROUTER } from "../../../services/api-config";
import { FaultTicket } from "../../fault-ticket/services/fault-ticket-service";

// ===== Enum (khớp BE order service — ADJUSTMENT_REQUEST_STATUS) =====
export enum ADJUSTMENT_REQUEST_STATUS {
  PENDING = 1,
  APPROVED = 2,
  REJECTED = 3,
}
export const ADJUSTMENT_REQUEST_STATUS_LABEL: Record<number, string> = {
  [ADJUSTMENT_REQUEST_STATUS.PENDING]: "Chờ duyệt",
  [ADJUSTMENT_REQUEST_STATUS.APPROVED]: "Đã duyệt",
  [ADJUSTMENT_REQUEST_STATUS.REJECTED]: "Đã từ chối",
};

export type QuantityAdjustmentStage = "counting" | "delivery_check";
export const STAGE_LABEL: Record<QuantityAdjustmentStage, string> = {
  counting: "Kiểm đếm với khách (khi nhận)",
  delivery_check: "Kiểm đếm khi giao",
};

export interface ProposedItem {
  category: string;
  quantity: number;
  category_id?: number | null;
}

export interface CountingAdjustmentRequest {
  id: number;
  order_id: number;
  branch_id: number;
  stage: QuantityAdjustmentStage;
  source_log_id: number;
  proposed_items: ProposedItem[];
  reason: string;
  images?: string[] | null;
  /** Số liệu CŨ (log đang xin sửa) — BE enrich để FE hiện cạnh số đề xuất, làm nổi bật phần lệch. */
  source_items?: ProposedItem[];
  requested_by: number;
  status: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_note: string | null;
  new_log_id: number | null;
  created_at: string;
  // Enrich ở BE (order-code/tên khách/tên nhân sự) — có ở mọi response list/detail/approve/reject.
  order_code?: string | null;
  customer_name?: string | null;
  requester_name?: string | null;
  reviewer_name?: string | null;
}

/** Lý do cho phần thiếu TĂNG THÊM khi duyệt điều chỉnh số kiểm đếm → loại bút toán sổ nợ đồ. */
export type DebtShortageReason = "OWE_CUSTOMER" | "LOST" | "NON_STANDARD";
export const DEBT_SHORTAGE_REASON_LABEL: Record<DebtShortageReason, string> = {
  OWE_CUSTOMER: "Nợ khách (trả bù ở đơn sau)",
  LOST: "Thất thoát",
  NON_STANDARD: "Hàng không chuẩn",
};

/** Sổ nợ đồ của đơn sẽ đổi thế nào nếu duyệt (BE `planDebtImpact`). */
export interface CountingDebtImpact {
  /** false = đơn chưa kiểm đếm giao → sổ chưa phát sinh, không cần chỉnh. */
  applicable: boolean;
  /** true = phần thiếu với khách TĂNG → bắt buộc chọn lý do khi duyệt. */
  reason_required: boolean;
  lines: {
    category: string;
    recorded: { owe: number; lost: number; non_standard: number };
    target: number;
    changes: { entry_type: number; entry_label: string; quantity: number }[];
    unresolved: number;
  }[];
  warnings: string[];
}

export type DebtAdjustmentRequestType = "debt" | "balance" | "backfill";
export const DEBT_REQUEST_TYPE_LABEL: Record<DebtAdjustmentRequestType, string> = {
  debt: "Điều chỉnh",
  balance: "Cân bằng đối soát",
  backfill: "Đồng bộ nợ cũ",
};

export interface DebtAdjustmentItem {
  category: string;
  category_id?: number | null;
  quantity: number;
}

export interface DebtAdjustmentRequest {
  id: number;
  customer_id: number;
  branch_id: number;
  request_type: DebtAdjustmentRequestType;
  items: DebtAdjustmentItem[];
  reason: string;
  requested_by: number;
  status: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_note: string | null;
  applied_log_ids: number[] | null;
  created_at: string;
  customer_name?: string | null;
  requester_name?: string | null;
  reviewer_name?: string | null;
}

export interface AdjustmentRequestFilters {
  order_id?: number;
  customer_id?: number;
  branch_id?: number;
  status?: number;
  page?: number;
  limit?: number;
}

export interface AdjustmentRequestListResponse<T> {
  list: T[];
  total_record: number;
  page: number;
  limit: number;
}

const AdjustmentRequestService = {
  // ===== Số lượng kiểm đếm =====
  listCounting: () => {
    const { request, baseResponse, loading } = useFetchData.Get<AdjustmentRequestListResponse<CountingAdjustmentRequest>>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: AdjustmentRequestFilters = {}) =>
        request({
          endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_LIST_COUNTING,
          data: { ...params, page: params.page ?? 1, limit: params.limit ?? 20 },
        }),
      response: baseResponse,
      loading,
    };
  },
  relatedFaultTickets: () => {
    const { request, baseResponse, loading } = useFetchData.Get<FaultTicket[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) => request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_RELATED_FAULT_TICKETS(id) }),
      response: baseResponse,
      loading,
    };
  },
  debtImpact: () => {
    const { request, baseResponse, loading } = useFetchData.Get<CountingDebtImpact>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) => request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_DEBT_IMPACT(id) }),
      response: baseResponse,
      loading,
    };
  },
  approveCounting: () => {
    const { request, baseResponse, loading } = useFetchData.Post<{
      request: CountingAdjustmentRequest;
      cancel_results: { fault_ticket_id: number; cancelled: boolean; error?: string }[];
      debt_impact?: CountingDebtImpact;
    }>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
    return {
      fetch: (
        id: number,
        payload: { review_note?: string; cancel_fault_ticket_ids?: number[]; debt_shortage_reason?: DebtShortageReason },
      ) =>
        request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_APPROVE_COUNTING(id), data: payload }),
      response: baseResponse,
      loading,
    };
  },
  rejectCounting: () => {
    const { request, baseResponse, loading } = useFetchData.Post<CountingAdjustmentRequest>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, review_note: string) =>
        request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_REJECT_COUNTING(id), data: { review_note } }),
      response: baseResponse,
      loading,
    };
  },

  // ===== Công nợ đồ =====
  createDebt: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DebtAdjustmentRequest>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (data: {
        customer_id: number;
        branch_id?: number;
        request_type?: DebtAdjustmentRequestType;
        items: DebtAdjustmentItem[];
        reason: string;
      }) => request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_CREATE_DEBT, data }),
      response: baseResponse,
      loading,
    };
  },
  listDebt: () => {
    const { request, baseResponse, loading } = useFetchData.Get<AdjustmentRequestListResponse<DebtAdjustmentRequest>>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: AdjustmentRequestFilters = {}) =>
        request({
          endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_LIST_DEBT,
          data: { ...params, page: params.page ?? 1, limit: params.limit ?? 20 },
        }),
      response: baseResponse,
      loading,
    };
  },
  approveDebt: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DebtAdjustmentRequest>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: { review_note?: string }) =>
        request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_APPROVE_DEBT(id), data: payload }),
      response: baseResponse,
      loading,
    };
  },
  rejectDebt: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DebtAdjustmentRequest>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, review_note: string) =>
        request({ endPoint: API_ADJUSTMENT_REQUEST_ROUTER.API_REJECT_DEBT(id), data: { review_note } }),
      response: baseResponse,
      loading,
    };
  },
};

export default AdjustmentRequestService;
